import React, { useEffect, useRef, useState } from 'react'
import { CButton, CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import JSZip from 'jszip'
import { icon } from '../../../shared/icons'
import { useBostonState, emit } from '../lib/useBoston'
import { state as bostonState, saveLibrary, FsAccess } from '../lib/store'
import { csLoadRuntimeTemplates, csBuildSingleFile } from '../lib/builder'
import { edApplyScript } from './EditorView'
import { useUI } from '../../../shared/ui/UIProvider'

/* Port fiel de viewLibrary/bindLibrary de boston_ia + helpers buildManifest y saveToLibrary.
   Biblioteca de scripts con rutas absolutas, manifest para el dashboard externo,
   rehorneado del runtime y export de todos los scripts como .html autocontenidos. */

/* === buildManifest — logica VERBATIM del original === */
export function buildManifest() {
  return {
    version: 1,
    generatedAt: new Date().toISOString(),
    baseFolder: bostonState.config.baseFolder,
    sabreSdk: 'agency-workspace-26.4',
    dashboard: bostonState.config.dashboardUrl || null,
    scripts: bostonState.library.map((s) => ({
      id: s.id,
      name: s.name,
      filename: s.filename,
      absolutePath: s.absolutePath,
      description: s.description,
      tags: s.tags || [],
      createdAt: s.createdAt,
      updatedAt: s.updatedAt,
      bytes: s.code.length,
    })),
  }
}

/* === saveToLibrary — logica VERBATIM del original (la usa el Creador para guardar el .ts).
   alert() → callback notify opcional; Storage.saveLibrary + renderSideFoot() → saveLibrary del store (emite). === */
export async function saveToLibrary(notify) {
  const folder = bostonState.config.baseFolder.replace(/[\\/]?$/, '\\')
  const path = folder + bostonState.scriptName + '.ts'
  const existing = bostonState.library.find((s) => s.absolutePath === path)
  const item = {
    id: existing?.id || `script-${Date.now()}`,
    name: bostonState.scriptName,
    description: bostonState.scriptDescription,
    tags: (bostonState.scriptTags || '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean),
    absolutePath: path,
    filename: bostonState.scriptName + '.ts',
    code: bostonState.scriptCode,
    createdAt: existing?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
  if (existing) saveLibrary(bostonState.library.map((s) => (s.id === existing.id ? item : s)))
  else saveLibrary([...bostonState.library, item])
  const wroteDirect = await FsAccess.write(item.filename, item.code).catch(() => false)
  const message = wroteDirect
    ? `Guardado y escrito a ${item.absolutePath}`
    : 'Guardado en biblioteca. Para escribir directo al disco, vincula la carpeta en Biblioteca → "Vincular carpeta destino".'
  if (notify) notify(message, wroteDirect ? 'success' : 'info')
  return { wroteDirect, item, message }
}

/* === Port de showPreviewModal (overlay DOM del original → CModal con iframe sandbox).
   Empaqueta el script como single-file en previewMode y lo carga en un iframe. === */
function PreviewModal({ dataJson, configJson, onClose }) {
  const title = (dataJson && dataJson.title) || bostonState.cs.title || 'Custom Script'
  const [url, setUrl] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    let blobUrl = null
    ;(async () => {
      try {
        // Forzar refresh del cache del runtime para que el preview SIEMPRE use la ultima app.js de disco
        await csLoadRuntimeTemplates(true)
        const singleHtml = await csBuildSingleFile(dataJson, configJson, { previewMode: true, title })
        blobUrl = URL.createObjectURL(new Blob([singleHtml], { type: 'text/html' }))
        if (alive) setUrl(blobUrl)
        else URL.revokeObjectURL(blobUrl)
      } catch (e) {
        console.error('[boston] error en preview:', e)
        if (alive) setError(e.message || String(e))
      }
    })()
    return () => {
      alive = false
      // Limpiar URL del blob cuando se cierre
      if (blobUrl) setTimeout(() => URL.revokeObjectURL(blobUrl), 100)
    }
    // eslint-disable-next-line
  }, [])

  function downloadProd() {
    // Generamos un .html SIN previewMode para el archivo descargable (es el de produccion)
    csBuildSingleFile(dataJson, configJson, { previewMode: false, title }).then((prodHtml) => {
      const pblob = new Blob([prodHtml], { type: 'text/html' })
      const purl = URL.createObjectURL(pblob)
      const a = document.createElement('a')
      a.href = purl
      const slug =
        (title + '')
          .toLowerCase()
          .replace(/[^a-z0-9_-]+/g, '_')
          .slice(0, 40) || 'custom_script'
      a.download = `${slug}.html`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(purl), 1000)
    })
  }

  return (
    <CModal visible size="xl" alignment="center" onClose={onClose}>
      <CModalHeader>
        <CModalTitle>
          <div style={{ fontSize: 11, opacity: 0.7, letterSpacing: '.5px', textTransform: 'uppercase' }}>
            Vista previa funcional · Single-file
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, marginTop: 2 }}>{title}</div>
        </CModalTitle>
      </CModalHeader>
      <div
        style={{
          background: '#fef3c7',
          color: '#92400e',
          padding: '8px 20px',
          fontSize: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          borderBottom: '1px solid #fcd34d',
        }}
      >
        <CIcon icon={icon('cilWarning')} />
        <span>
          <b>Preview mode:</b> las llamadas a Sabre están simuladas. El form, validación, postCommands y placeholders sí
          se ejecutan reales.
        </span>
      </div>
      <CModalBody style={{ padding: 0, height: '62vh', background: '#f8fafc' }}>
        {error ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              color: '#dc2626',
              font: '500 14px/1.5 system-ui',
            }}
          >
            <div>
              <div>
                <b>Error armando el preview</b>
              </div>
              <div style={{ fontSize: 12, marginTop: 6, opacity: 0.85 }}>{error}</div>
              <div style={{ fontSize: 11, marginTop: 8, opacity: 0.6 }}>Revisá la consola para detalles.</div>
            </div>
          </div>
        ) : url ? (
          <iframe
            title="Vista previa del script"
            src={url}
            sandbox="allow-scripts allow-forms allow-popups allow-modals allow-same-origin"
            style={{ width: '100%', height: '100%', border: 0, background: '#fff', display: 'block' }}
          />
        ) : (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              font: '500 14px/1.5 system-ui',
            }}
          >
            Empaquetando script en un único archivo…
          </div>
        )}
      </CModalBody>
      <CModalFooter style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 12, color: '#475569' }}>
          <b>{(dataJson && dataJson.forms && dataJson.forms.length) || 0}</b> form(s) ·{' '}
          <b>{(dataJson && dataJson.postCommands && dataJson.postCommands.length) || 0}</b> postCommand(s)
          <span style={{ opacity: 0.7, marginLeft: 12 }}>
            UUID: <code>{(configJson && configJson.config && configJson.config.uuid) || '—'}</code>
          </span>
        </span>
        <div className="d-flex gap-2">
          <CButton color="light" onClick={downloadProd} title="Descargar .html autocontenido">
            <CIcon icon={icon('cilCloudDownload')} /> Descargar HTML único
          </CButton>
          <CButton color="light" disabled={!url} onClick={() => window.open(url, '_blank')} title="Abrir en pestaña nueva">
            <CIcon icon={icon('cilExternalLink')} /> Pestaña nueva
          </CButton>
          <CButton color="danger" className="text-white" onClick={onClose} title="Cerrar">
            <CIcon icon={icon('cilX')} /> Cerrar
          </CButton>
        </div>
      </CModalFooter>
    </CModal>
  )
}

function LibraryView() {
  const state = useBostonState()
  const { toast, confirm } = useUI()
  const mockModeEnabled = new URLSearchParams(window.location.search).get('mock') === '1'
  const fileRef = useRef(null)
  const [preview, setPreview] = useState(null) // { dataJson, configJson }

  const sorted = state.library.slice().sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')))

  /* --- Vincular carpeta destino (File System Access API) --- */
  async function linkFolder() {
    try {
      const name = await FsAccess.pickDirectory()
      toast(`Carpeta vinculada: ${name}. Ahora "Guardar en biblioteca" escribe directo al disco.`, 'success')
      emit()
    } catch (e) {
      toast(`No se pudo vincular: ${e.message} (Funciona solo en Chromium con HTTPS o localhost.)`, 'danger')
    }
  }

  /* --- Exportar manifest.json --- */
  function exportManifest() {
    const manifest = buildManifest()
    const blob = new Blob([JSON.stringify(manifest, null, 2)], { type: 'application/json' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'scripts-manifest.json'
    a.click()
  }

  /* --- Importar manifest --- */
  async function importManifest(event) {
    const input = event.target
    const f = input.files && input.files[0]
    if (!f) return
    try {
      const data = JSON.parse(await f.text())
      if (!Array.isArray(data.scripts)) throw new Error('manifest invalido')
      for (const s of data.scripts) {
        if (!state.library.find((x) => x.absolutePath === s.absolutePath)) {
          state.library.push({ ...s, code: '// (importado solo metadata - copiar el .ts a mano)' })
        }
      }
      saveLibrary(state.library)
      toast(`Importados ${data.scripts.length} items.`, 'success')
    } catch (err) {
      toast(`Error: ${err.message}`, 'danger')
    } finally {
      input.value = ''
    }
  }

  /* --- Limpiar todo --- */
  async function clearLib() {
    const ok = await confirm({
      title: 'Limpiar biblioteca',
      body: '¿Borrar TODOS los scripts? (No borra los .ts del disco.)',
      confirmText: 'Borrar todo',
      cancelText: 'Cancelar',
      tone: 'danger',
    })
    if (!ok) return
    saveLibrary([])
  }

  /* --- Rehornear: regenera todos los scripts con el runtime actual --- */
  async function rebakeAll() {
    const eligible = state.library.filter((s) => s.dataJson)
    if (eligible.length === 0) {
      toast('No hay scripts con dataJson para rehornear.', 'warning')
      return
    }
    const ok = await confirm({
      title: 'Rehornear runtime',
      body: (
        <>
          <p>¿Rehornear {eligible.length} script(s)?</p>
          <p>
            Esto re-genera cada archivo con el runtime actual (app.js, api.js, styles.css), preservando el data.json y
            el UUID de cada script.
          </p>
          <p className="mb-0">
            Se regenera el .html autocontenido en localStorage. La publicacion remota queda pendiente para una fase posterior.
          </p>
        </>
      ),
      confirmText: 'Rehornear',
      cancelText: 'Cancelar',
      tone: 'success',
    })
    if (!ok) return

    let okCount = 0
    let failCount = 0
    const errors = []
    // Precargar templates una sola vez
    try {
      await csLoadRuntimeTemplates()
    } catch (e) {
      toast(`No se pudieron cargar los templates del runtime: ${e.message}`, 'danger')
      return
    }

    for (const item of eligible) {
      try {
        const dj = item.dataJson
        const cj = item.configJson || { config: { uuid: item.uuid, useInternalConfig: true } }
        // 1. Regenerar single-file en localStorage
        const singleHtml = await csBuildSingleFile(dj, cj, {
          previewMode: false,
          title: item.title || dj.title,
          name: item.name,
          uuid: item.uuid,
        })
        item.singleFileHtml = singleHtml
        item.singleFileBytes = singleHtml.length
        item.runtimeBakedAt = new Date().toISOString()
        item.updatedAt = new Date().toISOString()
        // 2. Publicacion remota pendiente: solo se actualiza localStorage.
        try {
          await Promise.resolve({
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: item.name, dataJson: dj, configJson: cj }),
          })
        } catch {
          /* ignore — el localStorage ya quedo actualizado */
        }
        okCount++
      } catch (e) {
        failCount++
        errors.push(`${item.name}: ${e.message}`)
      }
    }
    saveLibrary(state.library)
    let msg = `Rehorneado. OK: ${okCount} · Fallos: ${failCount}`
    if (errors.length) msg += ' — Errores: ' + errors.slice(0, 5).join(' | ')
    toast(msg, failCount ? 'warning' : 'success')
  }

  /* --- Exportar todos como .html en un ZIP --- */
  async function exportAllSingle() {
    const eligible = state.library.filter((s) => s.dataJson)
    if (eligible.length === 0) {
      toast('No hay scripts con dataJson para exportar.', 'warning')
      return
    }
    try {
      // JSZip viene bundleado (mismo paquete npm que usa lib/builder.js)
      const zip = new JSZip()
      const folder = zip.folder('boston_scripts_single')
      for (const item of eligible) {
        const dj = item.dataJson
        const cj = item.configJson || { config: { uuid: item.uuid, useInternalConfig: true } }
        const html =
          item.singleFileHtml ||
          (await csBuildSingleFile(dj, cj, {
            previewMode: false,
            title: item.title || dj.title,
            name: item.name,
            uuid: item.uuid,
          }))
        folder.file(`${item.name}.html`, html)
      }
      // Manifiesto resumen
      folder.file(
        '_manifest.json',
        JSON.stringify(
          {
            generatedAt: new Date().toISOString(),
            format: 'single-file-html',
            runtime: 'boston-ia-v1',
            scripts: eligible.map((s) => ({ name: s.name, title: s.title, uuid: s.uuid, file: `${s.name}.html` })),
          },
          null,
          2,
        ),
      )
      const blob = await zip.generateAsync({ type: 'blob' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `boston_scripts_single_${new Date().toISOString().slice(0, 10)}.zip`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(a.href), 1000)
      toast(`Exportados ${eligible.length} scripts como .html en un ZIP.`, 'success')
    } catch (e) {
      toast(`Error: ${e.message}`, 'danger')
    }
  }

  /* --- Acciones por item --- */
  /* Devuelve el dataJson del item, rescatando postCommands guardados aparte (items viejos). */
  function itemDataJson(item) {
    if (!item.dataJson) return null
    if (!item.dataJson.postCommands && Array.isArray(item.postCommands) && item.postCommands.length) {
      return { ...item.dataJson, postCommands: item.postCommands }
    }
    return item.dataJson
  }

  function editItem(item) {
    // FIX consistencia: cargar el data.json GUARDADO del item al Custom Script Builder.
    // (Antes saltaba a la vista 'creator', que ya no existe: el builder quedaba con el
    // script anterior y la vista previa de la edicion mostraba otra cosa.)
    const dj = itemDataJson(item)
    if (!dj) {
      toast('Este item no tiene dataJson asociado (es un .ts viejo): no se puede editar en el Builder.', 'warning')
      return
    }
    const cfg = item.configJson || { config: { uuid: item.uuid, useInternalConfig: true } }
    const info = edApplyScript(dj, cfg, item.name, item.appJs || null, item.fichaMd || null)
    toast(`"${item.name}" cargado en Custom Script para editar. UUID: ${info.uuid} · Forms: ${info.forms} · Commands: ${info.commands}`, 'success')
  }

  function previewItem(item) {
    if (!mockModeEnabled) return
    if (item.dataJson) {
      const cfg = item.configJson || { config: { uuid: 'preview-lib-' + Date.now(), useInternalConfig: true } }
      setPreview({ dataJson: itemDataJson(item), configJson: cfg })
    } else {
      toast('Este item de la biblioteca no tiene dataJson asociado (es un .ts viejo).', 'warning')
    }
  }

  function copyItem(item) {
    navigator.clipboard?.writeText(item.code)
  }

  async function downloadItem(item) {
    if (item.dataJson) {
      const cj = item.configJson || { config: { uuid: item.uuid, useInternalConfig: true } }
      const html =
        item.singleFileHtml ||
        (await csBuildSingleFile(item.dataJson, cj, {
          previewMode: false,
          title: item.title || item.dataJson.title,
          name: item.name,
          uuid: item.uuid,
        }))
      const a = document.createElement('a')
      a.href = URL.createObjectURL(new Blob([html], { type: 'text/html' }))
      a.download = item.name + '.html'
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(a.href), 1000)
    } else {
      await FsAccess.write(item.filename, item.code)
    }
  }

  async function delItem(item) {
    const ok = await confirm({
      title: 'Borrar script',
      body: `¿Borrar "${item.name}"?`,
      confirmText: 'Borrar',
      cancelText: 'Cancelar',
      tone: 'danger',
    })
    if (!ok) return
    saveLibrary(state.library.filter((s) => s.id !== item.id))
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Biblioteca de scripts</h1>
          <p>
            Scripts TypeScript guardados con sus <strong>rutas absolutas</strong>. El dashboard externo consume el{' '}
            <code>scripts-manifest.json</code> que genera esta vista.
          </p>
        </div>
        <div className="d-flex flex-wrap gap-2 justify-content-end">
          <CButton color="light" onClick={linkFolder}>
            <CIcon icon={icon('cilFolderOpen')} /> Vincular carpeta destino
          </CButton>
          <CButton color="light" onClick={exportManifest}>
            <CIcon icon={icon('cilDataTransferDown')} /> Exportar manifest.json
          </CButton>
          <CButton color="light" onClick={() => fileRef.current && fileRef.current.click()}>
            <CIcon icon={icon('cilDataTransferUp')} /> Importar manifest
          </CButton>
          <input type="file" accept=".json" ref={fileRef} hidden onChange={importManifest} />
          <CButton
            color="light"
            onClick={rebakeAll}
            title="Re-empaqueta todos los Custom Scripts con el runtime actual (propaga fixes de app.js/api.js a todos los scripts)"
          >
            <CIcon icon={icon('cilFire')} /> Rehornear runtime
          </CButton>
          <CButton
            color="light"
            onClick={exportAllSingle}
            title="Descarga TODOS los scripts como .html autocontenidos en un ZIP"
          >
            <CIcon icon={icon('cilCloudDownload')} /> Exportar todos como .html
          </CButton>
          <CButton color="danger" variant="outline" onClick={clearLib}>
            <CIcon icon={icon('cilTrash')} /> Limpiar todo
          </CButton>
        </div>
      </div>

      <div className="card-x" style={{ padding: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          <div>
            <strong>Total scripts</strong>
            <div style={{ fontSize: 24, color: 'var(--brand-dark)', fontWeight: 700 }}>{state.library.length}</div>
          </div>
          <div>
            <strong>Carpeta destino</strong>
            <code className="bia-mono" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
              {state.config.baseFolder}
            </code>
          </div>
          <div>
            <strong>Escritura disco</strong>
            <div style={{ marginTop: 4 }}>
              {FsAccess.dirHandle ? (
                <span className="bia-badge ok">vinculada a "{FsAccess.dirHandle.name}"</span>
              ) : (
                <span className="bia-badge warn">no vinculada · descarga manual</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="card-x" style={{ padding: 16 }}>
          <p className="bia-empty">
            No hay scripts guardados todavia.
            <br />
            <small>
              Andá al Creador, escribi tu script y dale <strong>Guardar en biblioteca</strong>.
            </small>
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14, alignItems: 'start' }}>
          {sorted.map((s) => (
            <div className="card-x" style={{ padding: 16, marginBottom: 0 }} key={s.id}>
              <div className="bia-card-head">
                <h3 style={{ wordBreak: 'break-all' }}>{s.name}.ts</h3>
                <span className="bia-pill mini">{(s.tags && s.tags[0]) || 'script'}</span>
              </div>
              <p style={{ margin: '6px 0' }}>{s.description || '(sin descripcion)'}</p>
              <code
                className="bia-mono"
                title={s.absolutePath}
                style={{
                  display: 'block',
                  fontSize: 11,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {s.absolutePath}
              </code>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 12, color: 'var(--muted)', margin: '8px 0' }}>
                <span>{String(s.updatedAt || '').slice(0, 10)}</span>
                <span>{(s.code || '').length} chars</span>
                {(s.tags || []).slice(1).map((t) => (
                  <span key={t}>· {t}</span>
                ))}
              </div>
              <div className="d-flex flex-wrap gap-1">
                <CButton size="sm" color="success" className="btn-brand" onClick={() => editItem(s)}>
                  <CIcon icon={icon('cilPencil')} /> Editar
                </CButton>
                {mockModeEnabled && s.dataJson ? (
                  <CButton size="sm" color="light" onClick={() => previewItem(s)}>
                    <CIcon icon={icon('cilZoomIn')} /> Vista previa
                  </CButton>
                ) : null}
                <CButton size="sm" color="light" onClick={() => copyItem(s)}>
                  <CIcon icon={icon('cilCopy')} /> Copy
                </CButton>
                <CButton size="sm" color="light" onClick={() => downloadItem(s)}>
                  <CIcon icon={icon('cilCloudDownload')} /> HTML único
                </CButton>
                <CButton size="sm" color="danger" variant="outline" onClick={() => delItem(s)} title="Borrar">
                  <CIcon icon={icon('cilTrash')} />
                </CButton>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card-x" style={{ padding: 16, marginTop: 14 }}>
        <div className="bia-card-head">
          <h3>Manifest para dashboard externo</h3>
          <span className="bia-pill">scripts-manifest.json</span>
        </div>
        <p style={{ marginTop: 0 }}>Estructura JSON que tu dashboard puede leer (HTTP GET o desde disco):</p>
        <pre className="bia-pre">{JSON.stringify(buildManifest(), null, 2)}</pre>
      </div>

      {preview ? (
        <PreviewModal dataJson={preview.dataJson} configJson={preview.configJson} onClose={() => setPreview(null)} />
      ) : null}
    </>
  )
}

export default LibraryView
